import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  PartnerDealBriefingSummaryResponse,
  PartnerDealBriefingVersionResponse,
  PartnerDealInvestigationProgress,
} from '@interfaces/partners/responses/partner-deal-briefing.response';
import {
  BriefingInvestigationStateEvent,
  CompanyInvestigationProgressEvent,
  PartnerDealChanges,
} from '@interfaces/realtime/events/partner-deal.events';
import {
  RealtimeEventTypes,
  RealtimeTopics,
} from '@interfaces/realtime/realtime-envelope.interface';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { RealtimeService } from '@services/realtime/realtime.service';
import { AiChatSidebarWrapperComponent } from '@src/app/features/dashboard/ai/components/ai-chat-sidebar-wrapper/ai-chat-sidebar-wrapper.component';
import { type ChatProductMode } from '@src/app/features/dashboard/ai/pages/chat/chat.types';
import { BRIEFING_CHAT_TOOL_LABELS, briefingChatSuggestions } from './briefing-chat';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import { Subscription, filter, finalize, first, merge, throttleTime } from 'rxjs';

/** Intervalo mínimo entre releituras da investigação pelo tempo real (o Cog avisa no máximo a cada 3 s; card 3918). */
const INVESTIGATION_REFRESH_THROTTLE_MS = 2_000;
import { describeBriefingError } from './briefing-errors';
import { BriefingInvestigationStatusComponent } from './briefing-investigation-status/briefing-investigation-status.component';
import { BriefingInvestigationStore } from './briefing-investigation.store';
import { BriefingPdfStore } from './briefing-pdf.store';
import { BriefingPreviewFrameComponent } from './briefing-preview-frame/briefing-preview-frame.component';
import { briefingPoll$ } from './briefing-poll';
import { BriefingPreviewStore } from './briefing-preview.store';
import {
  RevisionLabelContext,
  buildRevisionLabelContext,
  buildRevisionSummary,
  describeRevisionPath,
} from './briefing-revision';
import { BriefingRevisionSummaryComponent } from './briefing-revision-summary/briefing-revision-summary.component';
import { BriefingSectionPickerComponent } from './briefing-section-picker/briefing-section-picker.component';
import { BriefingVersionMenuComponent } from './briefing-version-menu/briefing-version-menu.component';
import {
  GENERATION_PHASES,
  READINESS_TITLES,
  briefingFillHeight,
  briefingPdfFileName,
  chatDockedWidthFor,
  documentCountLabel,
  failureReasonText,
  finalReviewSummary,
  generationCostView,
  generationPhaseIndex,
  investigationStateLabel,
  isActiveVersion,
  mentionsSlowGeneration,
  readinessBody,
  readinessTone,
  selectedInvestigationSubsections,
  sortVersions,
} from './briefing-presenter';
import {
  BRIEFING_INVESTIGATION_KEY,
  BRIEFING_TARGET_PAGES,
  BriefingFormat,
  applyShortcut,
  estimatePages,
  isSectionSelected,
  normalizeSelection,
  toggleSection,
  withoutInvestigation,
} from './briefing-sections';
import { PermissionsService } from '@services/helpers/permissions.service';
import {
  NO_GENERATE_PERMISSION_HINT,
  NO_INVESTIGATION_PERMISSION_HINT,
  NO_RERUN_PERMISSION_HINT,
  readBriefingPermissions,
} from './briefing-permissions';

/**
 * Largura mínima da janela para o chat do Cog IA abrir encostado nesta aba (B2 da crítica da opção B). Com o chat
 * aberto a aba entra em modo foco (Nova versão recolhe), então sobra só o leitor ao lado do chat: o painel encostado
 * tem largura própria e estreita ({@link chatDockedWidthFor}) e o documento se ajusta à largura que sobra. Abaixo de
 * 1.280px o chat abre por cima. As outras telas não mudam (o encostado delas continua com 35% da janela).
 */
const CHAT_DOCKED_MIN_VIEWPORT_WIDTH = 1280;

/** Largura útil a partir da qual as colunas ficam lado a lado (a mesma da container query `briefing-tab` do SCSS). */
const BRIEFING_SIDE_BY_SIDE_MIN_WIDTH = 1100;

/** Motivo dos botões de PDF desabilitados (tooltip): o PDF oficial só existe na versão pronta. */
export const PDF_NOT_READY_HINT =
  'Baixar e abrir o PDF ficam liberados quando a versão ficar pronta.';

/** Falha ao abrir a aba ou resposta vazia do estado do briefing. */
const LOAD_FALLBACK_MESSAGE =
  'Não foi possível abrir o briefing deste deal. Use Tentar de novo; se repetir, recarregue a página.';

/**
 * Aba **Briefing** do deal (card 3878), só para o time interno.
 *
 * Layout da opção A do protótipo (duas colunas), com a área da versão da opção B de "Versão e histórico" (Q11):
 * - esquerda: prontidão (mensagem do backend), seleção de seções e o botão Gerar;
 * - direita: o cartão compacto de fases quando há geração e o leitor da versão: barra com o menu "Versão N" (histórico,
 *   Ver e Tornar atual), Baixar e Abrir PDF em ícone e "Cog IA"; a prévia em HTML ao vivo (Q3) ou a falha; e, abaixo
 *   do documento, "O que mudou desde a vN" recolhido. Com o Cog IA aberto, modo foco: Nova versão recolhe num ícone.
 *
 * Estado vem do `GET partners/deals/{id}/briefing`; o tempo real (`partner-deal.changed` com
 * `change = briefing-changed`) só avisa, e a aba relê. Custo de IA só para ADMIN ("$" com popover na barra da versão). Regras e contrato no README
 * do módulo Comercial e no README do Briefing no backend.
 */
@Component({
  selector: 'app-deal-briefing-tab',
  standalone: true,
  imports: [
    SharedModule,
    RouterLink,
    VaultDateTimePipe,
    BriefingSectionPickerComponent,
    BriefingVersionMenuComponent,
    BriefingInvestigationStatusComponent,
    BriefingPreviewFrameComponent,
    BriefingRevisionSummaryComponent,
    AiChatSidebarWrapperComponent,
  ],
  // O HTML da visualização (1 a 1,5 MB por carga), os PDFs e a investigação vivem com a aba: sair dela solta tudo.
  providers: [BriefingPreviewStore, BriefingPdfStore, BriefingInvestigationStore],
  templateUrl: './deal-briefing-tab.component.html',
  styleUrl: './deal-briefing-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealBriefingTabComponent {
  private readonly partnersHttp = inject(PartnersHttpService);
  private readonly realtime = inject(RealtimeService);
  private readonly toast = inject(ToastHelperService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly preview = inject(BriefingPreviewStore);
  /** PDF oficial das versões (Abrir e Exportar PDF); ver `briefing-pdf.store.ts`. */
  protected readonly pdf = inject(BriefingPdfStore);
  /** Investigação da empresa do deal (card 3911); ver `briefing-investigation.store.ts`. */
  protected readonly investigation = inject(BriefingInvestigationStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly dealId = input.required<string>();
  /** Número do deal, para o nome do PDF (`briefing-<número>-v<versão>.pdf`). */
  readonly dealSequentialId = input<number | null>(null);
  /** Empresa do deal, mandada ao chat do Cog no cabeçalho `x-company-id`. */
  readonly companyId = input<string | null>(null);

  /** Uma permissão por ação (cards 3878 e 3911), as mesmas que a API confere; ver `briefing-permissions.ts`. */
  protected readonly can = readBriefingPermissions(inject(PermissionsService));
  protected readonly noGenerateHint = NO_GENERATE_PERMISSION_HINT;
  protected readonly noRerunHint = NO_RERUN_PERMISSION_HINT;
  /** Motivo da Investigação travada no seletor (sem `RUN`). */
  protected readonly lockedReasons = computed(
    (): Record<string, string> =>
      this.can.canRunInvestigation()
        ? {}
        : { [BRIEFING_INVESTIGATION_KEY]: NO_INVESTIGATION_PERMISSION_HINT }
  );

  protected readonly phases = GENERATION_PHASES;
  protected readonly readinessTitles = READINESS_TITLES;

  protected readonly summary = signal<PartnerDealBriefingSummaryResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly loadError = signal<string | null>(null);
  protected readonly selected = signal<string[]>([]);
  /** Formato do seletor: Resumido (padrão, toda seção ligada) ou Personalizado (interruptores no roteiro). */
  protected readonly format = signal<BriefingFormat>('summary');
  protected readonly targetPages = BRIEFING_TARGET_PAGES;
  /** Segmentos da barra de páginas do rodapé (1, 2, 3). */
  protected readonly pageSegments = Array.from({ length: BRIEFING_TARGET_PAGES }, (_, i) => i + 1);
  /** Versão aberta na coluna; nula = a escolha automática de {@link viewedVersion}. */
  private readonly viewedVersionId = signal<string | null>(null);
  protected readonly requesting = signal(false);
  protected readonly settingCurrentId = signal<string | null>(null);
  protected readonly chatOpen = signal(false);

  /** Caixa da investigação: o catálogo tem a seção (backend com o card 3911) e a pessoa tem `VIEW`. */
  protected readonly showInvestigationBox = computed(
    () =>
      this.can.canViewInvestigation() &&
      this.catalog().some(s => s.key === BRIEFING_INVESTIGATION_KEY && s.enabled)
  );
  protected readonly investigationSelected = computed(() =>
    this.selected().includes(BRIEFING_INVESTIGATION_KEY)
  );
  /**
   * Custo de IA da geração da versão (pedido do Victor): só quando o backend manda o campo, e o template ainda exige o
   * papel ADMIN (`*appHasRole`). Os outros papéis não veem nada.
   */
  protected generationCost(version: PartnerDealBriefingVersionResponse) {
    return generationCostView(version.generationCost);
  }

  /** Versão com a lista da revisão final aberta ("Conferido pelo Cog IA"). */
  protected readonly finalReviewOpenId = signal<string | null>(null);

  /** "Conferido pelo Cog IA: N ajustes" da versão pronta (Q13); nulo sem revisão ou revisão que não terminou. */
  protected finalReviewText(version: PartnerDealBriefingVersionResponse): string | null {
    return version.status === 'Ready' ? finalReviewSummary(version.finalReview) : null;
  }

  /** Ajustes aplicados pela revisão final, com o lugar em linguagem de negócio (o mesmo do "O que mudou"). */
  protected finalReviewItems(
    version: PartnerDealBriefingVersionResponse
  ): { place: string; correction: string }[] {
    const labels = this.revisionLabels();
    const context = labels && labels.versionId === version.id ? labels.labels : null;
    return (version.finalReview?.items ?? [])
      .filter(item => item.applied)
      .map(item => ({
        place: describeRevisionPath(item.path, this.catalog(), context),
        correction: item.fix,
      }));
  }

  protected toggleFinalReview(version: PartnerDealBriefingVersionResponse): void {
    this.finalReviewOpenId.update(id => (id === version.id ? null : version.id));
  }

  /** Aviso da investigação na versão aberta (Investigando, levando para o texto, não concluída). */
  protected investigationNotice(version: PartnerDealBriefingVersionResponse): string | null {
    const state = version.investigationState;
    if (state !== 'waiting' && state !== 'adjusting' && state !== 'not_completed') return null;
    return investigationStateLabel(state);
  }

  protected readonly versions = computed(() => sortVersions(this.summary()?.versions ?? []));
  protected readonly catalog = computed(() => this.summary()?.catalog ?? []);
  /** O catálogo que a pessoa pode marcar: sem `RUN`, a Investigação e as subseções ficam desligadas. */
  protected readonly selectableCatalog = computed(() =>
    this.can.canRunInvestigation() ? this.catalog() : withoutInvestigation(this.catalog())
  );
  protected readonly readiness = computed(() => this.summary()?.readiness ?? null);
  protected readonly tone = computed(() => {
    const readiness = this.readiness();
    return readiness ? readinessTone(readiness.status) : 'blocked';
  });
  protected readonly documentsToRead = computed(
    () => this.readiness()?.missingProcessedDocuments ?? []
  );
  /** Mensagem do backend sem repetir o título da caixa (G1). */
  protected readonly readinessMessage = computed(() =>
    readinessBody(READINESS_TITLES[this.tone()], this.readiness()?.message)
  );
  protected readonly readinessMentionsSlowness = computed(() =>
    mentionsSlowGeneration(this.readinessMessage())
  );
  /** Motivo da falha sem jargão de bastidor (N3). */
  protected readonly failureReason = failureReasonText;
  protected readonly activeVersion = computed(() => this.versions().find(isActiveVersion) ?? null);
  protected readonly currentVersion = computed(() => {
    const id = this.summary()?.currentVersionId;
    return this.versions().find(v => v.id === id || (id == null && v.isCurrent)) ?? null;
  });

  /**
   * A versão que a coluna mostra: a escolhida na lista; sem escolha, a que está gerando (rascunho ao vivo, Q3); senão
   * a mais nova que falhou depois da atual (para a pessoa ver o motivo); senão a atual.
   */
  protected readonly viewedVersion = computed<PartnerDealBriefingVersionResponse | null>(() => {
    const versions = this.versions();
    const chosen = versions.find(v => v.id === this.viewedVersionId());
    if (chosen) return chosen;

    const active = this.activeVersion();
    if (active) return active;

    const current = this.currentVersion();
    const latestDone = versions.find(v => !isActiveVersion(v));
    if (
      latestDone?.status === 'Failed' &&
      (!current || latestDone.versionNumber > current.versionNumber)
    ) {
      return latestDone;
    }
    return current;
  });

  protected readonly activePhaseIndex = computed(() => {
    const active = this.activeVersion();
    return active ? generationPhaseIndex(active) : -1;
  });

  /** Seções do primeiro nível que entram (as subseções da Investigação contam dentro dela). */
  protected readonly selectedCount = computed(
    () =>
      this.selectableCatalog().filter(s => !s.parentKey && isSectionSelected(s, this.selected()))
        .length
  );

  /** Estimativa de páginas para o rodapé (aviso, não bloqueio). */
  protected readonly pageEstimate = computed(() =>
    estimatePages(this.selected(), this.selectableCatalog())
  );

  /** A janela tem a largura mínima para o chat encostar (atualizado só com o chat aberto). */
  private readonly chatDocked = signal(false);
  private chatMediaQuery: MediaQueryList | null = null;
  /** Largura do chat encostado no modo foco (acompanha o resize da janela). */
  protected readonly chatDockedWidth = signal(
    chatDockedWidthFor(typeof window === 'undefined' ? 1920 : window.innerWidth)
  );
  private readonly onChatMediaChange = (event: MediaQueryListEvent): void =>
    this.chatDocked.set(event.matches);

  /** Chat encostado só com espaço de sobra; senão por cima do conteúdo (ver {@link CHAT_DOCKED_MIN_VIEWPORT_WIDTH}). */
  protected readonly chatMode = computed<'docked' | 'overlay'>(() =>
    this.chatDocked() ? 'docked' : 'overlay'
  );

  /** Gerar e Tentar de novo: exige `PARTNERDEALBRIEFING_GENERATE`, prontidão liberada e nenhuma geração ativa. */
  protected readonly canGenerate = computed(
    () =>
      this.can.canGenerate() &&
      !!this.summary() &&
      this.tone() !== 'blocked' &&
      !this.activeVersion() &&
      !this.requesting()
  );

  /** Visualização em HTML da versão aberta (Q3): HTML, esqueleto, erro ou indisponível (versão que falhou). */
  protected readonly previewView = computed(() => this.preview.view(this.viewedVersion()));

  protected readonly pdfNotReadyHint = PDF_NOT_READY_HINT;

  /**
   * Rótulos de negócio do "O que mudou" (cards, pilares e figuras), lidos do contexto do briefing do deal (versão
   * atual). Nunca se mostra id de figura nem chave de campo; sem os rótulos, troca de figura não aparece.
   */
  private readonly revisionLabels = signal<{
    versionId: string | null;
    labels: RevisionLabelContext;
  } | null>(null);
  private revisionLabelsRequested = false;

  /** "O que mudou desde a v{n}" da versão aberta (ajuste entregue); nulo nas demais. */
  protected readonly revisionSummary = computed(() => {
    const viewed = this.viewedVersion();
    const source = this.revisionLabels();
    // Cards e pilares só valem para a versão de onde os rótulos vieram; figuras valem para o deal.
    const labels: RevisionLabelContext | null = source
      ? source.versionId === viewed?.id
        ? source.labels
        : { cardLabels: [], pillarTitles: [], figureLabels: source.labels.figureLabels }
      : null;
    return buildRevisionSummary(viewed, this.versions(), this.catalog(), labels);
  });

  /**
   * Versão pronta aberta pelo menu que não é a atual (Q11): o leitor avisa "Você está vendo a vN; a atual é a vM" e
   * oferece Tornar atual. Ver não troca a atual sozinho.
   */
  protected readonly olderVersionNotice = computed(() => {
    const viewed = this.viewedVersion();
    const current = this.currentVersion();
    if (!viewed || !current || viewed.id === current.id || viewed.status !== 'Ready') return null;
    return { viewedNumber: viewed.versionNumber, currentNumber: current.versionNumber };
  });

  /** Painel Nova versão reaberto no modo foco (chat aberto); fora do foco ele aparece sempre. */
  protected readonly generatePanelOpen = signal(false);

  /** Cartão do leitor, para rolar até a prévia quando o chat abre (modo foco). */
  private readonly viewer = viewChild<ElementRef<HTMLElement>>('viewer');
  /** Invólucro das colunas: a largura dele diz se ficam lado a lado, e o topo dá a altura até o fim da janela. */
  private readonly shell = viewChild<ElementRef<HTMLElement>>('shell');

  /** Altura das colunas até o fim da janela (`briefingFillHeight`), medida no resize e na troca de largura. */
  protected readonly fillHeight = signal(
    briefingFillHeight(typeof window === 'undefined' ? 900 : window.innerHeight, 0, 62)
  );
  /** Colunas lado a lado (largura útil acima de 1.100px, a mesma régua da container query do SCSS). */
  protected readonly sideBySide = signal(true);
  /**
   * O que o leitor gasta além do palco da prévia: barra da versão (~57px), aviso de versão antiga (~45px), pé (~41px)
   * e bordas. Vira o mínimo do palco; o palco cresce até ocupar o resto.
   */
  protected readonly previewChrome = 150;
  /** Páginas da prévia, para o pé do leitor ("· 3 páginas"). */
  protected readonly previewPages = signal<number | null>(null);
  private layoutObserver: ResizeObserver | null = null;
  private readonly onWindowResize = (): void => this.measureLayout();

  /**
   * Chat do briefing sempre em modo produto, para todo mundo (contrato `ChatProductMode` do card 3886): título do
   * agente, sem custo de IA (`$`), debug, tokens, raciocínio nem nome técnico de ferramenta. Os títulos das ferramentas
   * vêm do registro `chat-tool-definitions.ts` (`tool-<nome>`). Título, subtítulo, placeholder e sugestões seguem o
   * contrato da Q10 (`task-Q10-report.md`, "Contrato das sugestões para o front").
   */
  protected readonly chatProductMode = computed<ChatProductMode>(() => {
    // Sem `PARTNERDEALBRIEFING_ADJUST` o chat só responde perguntas: a API recusa o pedido de ajuste.
    const canAdjust = this.can.canAdjust();
    return {
      title: 'Cog IA · Briefing',
      subtitle: canAdjust
        ? 'Assistente do deal: tira dúvidas com os dados da empresa e ajusta o briefing'
        : 'Assistente do deal: tira dúvidas com os dados da empresa e do briefing',
      toolLabels: BRIEFING_CHAT_TOOL_LABELS,
      fallbackToolLabel: 'Consultando os dados',
      placeholder: canAdjust
        ? 'Pergunte sobre a empresa ou peça um ajuste no briefing'
        : 'Pergunte sobre a empresa ou o briefing',
      restartDescription:
        'A conversa com o Cog será apagada. O briefing e as versões continuam como estão.',
      suggestions: briefingChatSuggestions(
        this.currentVersion()?.status === 'Ready',
        this.revisionLabels()?.labels.decision ?? null,
        canAdjust
      ),
    };
  });

  /**
   * Contexto que o chat do Cog recebe a cada mensagem (`screenContext`). O Cog só precisa do `dealId`: ele mesmo lê
   * o contexto do briefing no backend com o token de quem conversa. A versão vai junto para o log da conversa.
   */
  protected readonly chatContextProvider = (): unknown => ({
    dealId: this.dealId(),
    versionId: this.currentVersion()?.id ?? null,
    versionNumber: this.currentVersion()?.versionNumber ?? null,
  });

  private realtimeSubscription: Subscription | null = null;
  private loadSubscription: Subscription | null = null;
  /** Última versão em geração vista, para limpar a escolha manual quando nasce outra. */
  private lastActiveVersionId: string | null = null;

  constructor() {
    effect(() => {
      const dealId = this.dealId();
      untracked(() => this.startDeal(dealId));
    });
    // Investigação só com `VIEW`: sem ela, nada de GET (e de 403) na abertura nem nos eventos. A permissão chega
    // depois do primeiro desenho, então o store busca quando ela vira `true`.
    effect(() => {
      const dealId = this.dealId();
      const canView = this.can.canViewInvestigation();
      untracked(() => this.investigation.start(dealId, canView));
    });
    // Versão aberta com ajuste: lê os rótulos do contexto do briefing (uma vez por deal e por versão atual).
    effect(() => {
      const viewed = this.viewedVersion();
      const currentId = this.currentVersion()?.id ?? null;
      if (viewed?.revision?.mode !== 'edit') return;
      untracked(() => this.loadRevisionLabels(currentId));
    });

    // A visualização acompanha a versão aberta: cada GET (tempo real) traz o `previewUpdatedAt`; mudou, recarrega o
    // HTML (o store guarda por versão e por instante, então releitura sem rascunho novo não baixa nada).
    effect(() => {
      const version = this.viewedVersion();
      const activeId = this.activeVersion()?.id;
      untracked(() => this.preview.load(this.dealId(), version, [activeId]));
    });

    // Tempo real fora (ticket recusado, hub caído) ou evento perdido: enquanto há versão na fila ou gerando, relê a
    // cada 8 s sem conexão e a cada 30 s com ela. Para sozinho quando nenhuma versão está ativa. O `load(true)` é o
    // mesmo do evento, que já descarta a releitura anterior.
    briefingPoll$(toObservable(computed(() => !!this.activeVersion())), this.realtime.status$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(true));

    // Versão nova em geração (Gerar da aba ou ajuste pedido pelo chat): a escolha manual na lista deixa de valer,
    // para a coluna mostrar o rascunho ao vivo (Menor 6 da revisão).
    effect(() => {
      const activeId = this.activeVersion()?.id ?? null;
      untracked(() => {
        if (activeId && activeId !== this.lastActiveVersionId) this.viewedVersionId.set(null);
        this.lastActiveVersionId = activeId;
      });
    });

    // Altura das colunas (Q11): o invólucro aparece quando o resumo chega; dali em diante a troca de largura (chat
    // encostado, janela) e o resize da janela remedem.
    effect(() => {
      const shell = this.shell()?.nativeElement ?? null;
      untracked(() => this.observeLayout(shell));
    });

    // Os stores (prévia, PDF) se soltam sozinhos no `ngOnDestroy` deles.
    this.destroyRef.onDestroy(() => {
      this.observeLayout(null);
      this.realtimeSubscription?.unsubscribe();
      this.loadSubscription?.unsubscribe();
      this.unwatchChatViewport();
    });
  }

  /** Relê o estado da aba. `silent`: sem a tela de carregamento (tempo real, depois de uma ação). */
  protected load(silent = false): void {
    if (!silent) {
      this.loading.set(true);
      this.loadError.set(null);
    }

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.partnersHttp
      .getDealBriefing(this.dealId())
      .pipe(first())
      .subscribe({
        next: summary => {
          this.loading.set(false);
          // Resposta vazia (corpo nulo, renderização no servidor) não pode deixar a aba em branco (M4).
          if (!summary) {
            if (!silent || !this.summary()) this.loadError.set(LOAD_FALLBACK_MESSAGE);
            return;
          }
          this.summary.set(summary);
          this.loadError.set(null);
          // Resumido acompanha o catálogo (seção religada por configuração entra sozinha); o Personalizado guarda
          // a escolha da pessoa, só normalizada.
          const catalog = this.selectableCatalog();
          this.selected.set(
            this.format() === 'custom'
              ? normalizeSelection(this.selected(), catalog)
              : applyShortcut(catalog, 'summary')
          );
        },
        error: err => {
          this.loading.set(false);
          // Releitura silenciosa que falha mantém o que já está na tela.
          if (!silent || !this.summary()) {
            this.loadError.set(this.describeError(err, LOAD_FALLBACK_MESSAGE));
          }
        },
      });
  }

  /** Interruptor de uma opcional no roteiro (só existe no Personalizado). */
  protected onToggle(key: string): void {
    if (this.format() !== 'custom') return;
    this.selected.set(toggleSection(this.selected(), key, this.selectableCatalog()));
  }

  /**
   * Troca de formato. Resumido volta para toda seção ligada; Personalizado parte da seleção atual (as do Resumido),
   * para a pessoa só desligar o que não quer.
   */
  protected onFormat(format: BriefingFormat): void {
    this.format.set(format);
    if (format === 'summary') {
      this.selected.set(applyShortcut(this.selectableCatalog(), 'summary'));
    }
  }

  protected onViewVersion(versionId: string): void {
    this.viewedVersionId.set(versionId);
  }

  protected viewCurrent(): void {
    this.viewedVersionId.set(this.currentVersion()?.id ?? null);
  }

  /** Gerar com as seções marcadas. */
  protected generate(): void {
    this.requestGeneration(this.selected());
  }

  /** Tentar de novo a versão que falhou, com as mesmas seções dela. */
  protected retry(version: PartnerDealBriefingVersionResponse): void {
    this.requestGeneration(version.selectedSections ?? []);
  }

  protected setCurrent(versionId: string): void {
    const version = this.versions().find(v => v.id === versionId);
    if (!version || this.settingCurrentId() || !this.can.canSetCurrent()) return;

    this.settingCurrentId.set(versionId);
    this.partnersHttp
      .setCurrentDealBriefing(this.dealId(), versionId)
      .pipe(
        first(),
        finalize(() => this.settingCurrentId.set(null))
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            'Versão atual trocada',
            `A versão ${version.versionNumber} agora é a atual: é ela que o chat do Cog IA lê.`
          );
          this.viewedVersionId.set(versionId);
          this.load(true);
        },
        error: err =>
          this.toast.showToastError(
            'Não foi possível trocar a versão atual',
            this.describeError(err, 'Tente de novo em instantes.')
          ),
      });
  }

  protected openPdf(version: PartnerDealBriefingVersionResponse): void {
    this.pdf.open(this.dealId(), version, this.pdfFileName(version));
  }

  protected downloadPdf(version: PartnerDealBriefingVersionResponse): void {
    this.pdf.download(this.dealId(), version, this.pdfFileName(version));
  }

  private pdfFileName(version: PartnerDealBriefingVersionResponse): string {
    return briefingPdfFileName(this.dealSequentialId(), version.versionNumber);
  }

  /** "Tentar de novo" da visualização (502 do Doc-Generate, rede). */
  protected retryPreview(version: PartnerDealBriefingVersionResponse): void {
    this.preview.retry(this.dealId(), version);
  }

  /** Abre o Cog IA em modo foco: Nova versão recolhe e a página rola até a prévia (Q11). */
  protected openChat(): void {
    this.watchChatViewport();
    // A sugestão "Por que a recomendação é X?" precisa da decisão da versão atual (vem do chat-context).
    const current = this.currentVersion();
    if (current?.status === 'Ready') this.loadRevisionLabels(current.id);
    this.generatePanelOpen.set(false);
    this.chatOpen.set(true);
    // Depois do layout do foco (o painel recolhe e a coluna muda de largura).
    setTimeout(() =>
      this.viewer()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  }

  /** Fechar o chat (ou "Sair do foco") volta ao layout normal. */
  protected closeChat(): void {
    this.chatOpen.set(false);
    this.generatePanelOpen.set(false);
    this.unwatchChatViewport();
  }

  /** Liga (ou solta, com `null`) a medição das colunas no invólucro. */
  private observeLayout(shell: HTMLElement | null): void {
    this.layoutObserver?.disconnect();
    this.layoutObserver = null;
    if (typeof window === 'undefined') return;
    window.removeEventListener('resize', this.onWindowResize);
    if (!shell) return;

    window.addEventListener('resize', this.onWindowResize, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.layoutObserver = new ResizeObserver(() => this.measureLayout());
      this.layoutObserver.observe(shell);
    }
    this.measureLayout();
  }

  /**
   * Lado a lado pela largura do invólucro; altura pela janela, menos o menu do topo e a distância das abas do deal até
   * as colunas (o que fica acima delas com a página rolada até as abas). Formatação de layout, sem regra de negócio.
   */
  private measureLayout(): void {
    const shell = this.shell()?.nativeElement;
    if (!shell || typeof window === 'undefined') return;

    this.sideBySide.set(shell.clientWidth > BRIEFING_SIDE_BY_SIDE_MIN_WIDTH);
    const dockedWidth = chatDockedWidthFor(window.innerWidth);
    if (dockedWidth !== this.chatDockedWidth()) this.chatDockedWidth.set(dockedWidth);

    const shellTop = shell.getBoundingClientRect().top;
    // As abas do deal ficam na tela do deal, fora deste componente; sem elas, mede a partir do próprio invólucro.
    const tabs = shell.ownerDocument.querySelector('[role="tablist"]');
    const offsetFromTabs = tabs ? Math.max(0, shellTop - tabs.getBoundingClientRect().top) : 0;
    const headerOffset =
      parseFloat(
        getComputedStyle(shell.ownerDocument.documentElement).getPropertyValue(
          '--dashboard-header-offset'
        )
      ) || 0;
    const height = briefingFillHeight(window.innerHeight, headerOffset, offsetFromTabs);
    if (height !== this.fillHeight()) this.fillHeight.set(height);
  }

  /** Ícone "Nova versão" da barra do leitor no modo foco. */
  protected toggleGeneratePanel(): void {
    this.generatePanelOpen.update(open => !open);
  }

  /**
   * Enquanto o chat está aberto, escuta só a travessia do limiar de largura (`matchMedia`, evento `change`), e não
   * cada `resize`: arrastar a borda da janela não dispara detecção de mudanças à toa (achado 8 da revisão do Q4). O
   * chat troca entre encostado e por cima sem fechar.
   */
  private watchChatViewport(): void {
    if (typeof window === 'undefined' || this.chatMediaQuery) return;
    this.chatMediaQuery = window.matchMedia(`(min-width: ${CHAT_DOCKED_MIN_VIEWPORT_WIDTH}px)`);
    this.chatDocked.set(this.chatMediaQuery.matches);
    this.chatMediaQuery.addEventListener('change', this.onChatMediaChange);
  }

  private unwatchChatViewport(): void {
    this.chatMediaQuery?.removeEventListener('change', this.onChatMediaChange);
    this.chatMediaQuery = null;
  }

  protected documentCount(count: number): string {
    return documentCountLabel(count);
  }

  /** Lê os rótulos do "O que mudou" do contexto do briefing; falha deixa as linhas de figura escondidas. */
  private loadRevisionLabels(currentVersionId: string | null): void {
    const loaded = this.revisionLabels();
    if (this.revisionLabelsRequested && (!loaded || loaded.versionId === currentVersionId)) return;
    this.revisionLabelsRequested = true;
    const dealId = this.dealId();
    this.partnersHttp
      .getDealBriefingChatContext(dealId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: context => {
          if (!context || this.dealId() !== dealId) return;
          this.revisionLabels.set({
            versionId: context.versionId,
            labels: buildRevisionLabelContext(context.content, context.base),
          });
        },
        error: () => undefined,
      });
  }

  private startDeal(dealId: string): void {
    this.realtimeSubscription?.unsubscribe();
    this.revisionLabels.set(null);
    this.revisionLabelsRequested = false;
    this.pdf.reset();
    this.preview.reset();
    this.summary.set(null);
    this.viewedVersionId.set(null);
    this.format.set('summary');
    this.closeChat();
    // A investigação troca de deal pelo efeito do `start` (que também confere o `VIEW`).
    this.load();

    // Evento é dica, REST é a verdade: cada fase, versão pronta, falha ou troca de atual relê a aba; o estado da
    // investigação numa versão (card 3911) também relê, junto com a investigação.
    const topic = RealtimeTopics.partnerDeal(dealId);
    const briefingChanged$ = this.realtime
      .on(RealtimeEventTypes.PartnerDealChanged, topic)
      .pipe(filter(envelope => envelope.payload?.change === PartnerDealChanges.BriefingChanged));
    const investigationState$ = this.realtime.on(BriefingInvestigationStateEvent, topic);
    this.realtimeSubscription = merge(briefingChanged$, investigationState$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.load(true);
        this.investigation.refresh();
      });

    // A investigação mudou (criada, progresso a cada 3 s, pronta, falhou; cards 3911 e 3918): relê só a investigação,
    // que traz etapas, atividade e subseções. No máximo uma leitura a cada 2 s, sempre com a última notícia.
    this.realtimeSubscription.add(
      this.realtime
        .on(RealtimeEventTypes.PartnerDealChanged, topic)
        .pipe(
          filter(envelope => envelope.payload?.change === PartnerDealChanges.InvestigationChanged),
          throttleTime(INVESTIGATION_REFRESH_THROTTLE_MS, undefined, {
            leading: true,
            trailing: true,
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.investigation.refresh())
    );

    // Progresso ao vivo da investigação: fase e números na caixa; a primeira notícia relê o estado (sai da fila).
    this.realtimeSubscription.add(
      this.realtime
        .on<PartnerDealInvestigationProgress>(CompanyInvestigationProgressEvent, topic)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(envelope => this.investigation.applyProgress(envelope.payload ?? null))
    );
  }

  /** "Refazer investigação": pesquisa de novo com as subseções marcadas (as ligadas na configuração se nenhuma). */
  protected rerunInvestigation(): void {
    if (!this.can.canRerunInvestigation()) return;
    this.investigation.rerun(selectedInvestigationSubsections(this.selected(), this.catalog()));
  }

  /** "Completar investigação" da versão; com o pedido aceito, a aba relê as versões. */
  protected completeInvestigation(version: PartnerDealBriefingVersionResponse): void {
    if (!this.can.canRerunInvestigation()) return;
    this.investigation.complete(version.id, () => this.load(true));
  }

  /**
   * Pede a geração. Com prontidão "com leitura de documento", confirma antes: o agente vai ler os PDFs do Data Room,
   * demora mais e os números saem marcados como lidos do documento.
   */
  private requestGeneration(requested: readonly string[]): void {
    if (!this.canGenerate()) return;
    // Só o que a pessoa pode marcar: sem `RUN`, a Investigação sai do pedido (inclusive no Tentar de novo).
    const sections = normalizeSelection(requested, this.selectableCatalog());

    if (this.tone() === 'reading') {
      const docs = this.documentsToRead();
      const count = documentCountLabel(docs.length);
      this.confirmation
        .confirm(
          `Gerar lendo ${count} direto?`,
          `Ainda não há exercício fechado lido. O agente vai ler ${count} do Data Room${
            docs.length ? ` (${docs.join(', ')})` : ''
          }, e a geração demora mais. Os números lidos assim saem marcados como "lido do documento" no briefing. Se preferir, espere a leitura dos documentos terminar e gere depois.`,
          'default',
          'Esperar a leitura',
          'Gerar mesmo assim'
        )
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe(confirmed => {
          if (confirmed) this.sendGeneration(sections);
        });
      return;
    }

    this.sendGeneration(sections);
  }

  private sendGeneration(sections: string[]): void {
    this.requesting.set(true);
    this.partnersHttp
      .generateDealBriefing(this.dealId(), sections)
      .pipe(
        first(),
        finalize(() => this.requesting.set(false))
      )
      .subscribe({
        next: () => {
          this.viewedVersionId.set(null);
          this.toast.showToastSuccess(
            'Briefing pedido',
            'O Cog IA começou a escrever. O rascunho aparece na própria aba e se atualiza sozinho; pode sair dela sem perder nada.'
          );
          this.load(true);
        },
        error: err =>
          this.toast.showToastError(
            'Não foi possível gerar o briefing',
            this.describeError(err, 'Tente de novo em instantes.')
          ),
      });
  }

  /** Mensagem do backend quando há; senão o texto da tela (ver `describeBriefingError`). */
  private describeError(err: unknown, fallback: string): string {
    return describeBriefingError(err, fallback);
  }
}
